import { useP1TimelineMini } from '../../hooks/use-p1';
import { adaptTimeline } from '../../telas/painel-p1/tela8-adapter';
import { P1CategoryBadge } from '../badges/P1CategoryBadge';

import { EscalationChainCard } from './EscalationChainCard';
import { SequenceTimelineMini } from './SequenceTimelineMini';

import type { P1CardVM } from '../../view-models';
import type React from 'react';

export interface P1CardProps {
  card: P1CardVM;
  /** Nome resolvido (via usePresence na página) de quem reconheceu/tratou. DA: pode faltar. */
  engagedByName?: string;
  /** T8-2 — handlers de ação. Quando ausentes, o card é read-only (sem footer). */
  onAcompanhar?: (card: P1CardVM) => void;
  onRunbook?: (card: P1CardVM) => void;
  onOpenDetail?: (card: P1CardVM) => void;
  /** Item 330 — abre a substituição de atendente (reatribuir a conversa) pelo drawer. */
  onReassign?: (card: P1CardVM) => void;
  /** Abre o espelho da conversa do incidente (Tela 2); ausente = sem affordance. */
  onOpenConversation?: (conversationId: string) => void;
  /** Gates de capability (P19): esconder, não desabilitar morto. */
  canAcknowledge?: boolean;
  canRunbook?: boolean;
  /** conversation-assign — habilita "Substituir atendente" (Item 330). */
  canAssign?: boolean;
  /** Anti duplo-submit do Acompanhar. */
  acknowledging?: boolean;
}

/**
 * Card de incidente P1 (.p1-card) — converte cards-3.jsx.
 * T8-1 read-only: SEM footer de ações (entra no T8-2, D6).
 * DA: o `list` não traz cliente/bond/atendente/descrição → card enxuto e honesto
 * (título = Conversa #id; sem descrição livre). A timeline-mini é buscada pelo
 * próprio card só p/ level4 (hook estável por card montado).
 */
export function P1Card({
  card,
  engagedByName,
  onAcompanhar,
  onRunbook,
  onOpenDetail,
  onReassign,
  onOpenConversation,
  canAcknowledge = false,
  canRunbook = false,
  canAssign = false,
  acknowledging = false,
}: P1CardProps): React.JSX.Element {
  const timeline = useP1TimelineMini(card.id, card.isLevel4);
  const timelineVM = timeline.data != null ? adaptTimeline(timeline.data) : null;

  const interactive =
    onAcompanhar !== undefined ||
    onRunbook !== undefined ||
    onOpenDetail !== undefined ||
    onReassign !== undefined;
  const showRunbook = canRunbook && card.runbookSlug !== null && onRunbook !== undefined;
  const showReassign = canAssign && onReassign !== undefined;

  return (
    <article className={`p1-card p1-card--${card.tone}`}>
      <header className="p1-card__head">
        <P1CategoryBadge category={card.category} />
        <span style={{ flex: 1 }} />
        <span className="tweaks-hint mono">detectado há {card.detectedAgoLabel}</span>
        <span className="tweaks-hint">confiança {card.confidencePct}%</span>
      </header>

      <h3 className="p1-card__title">Conversa em incidente</h3>

      <div className="p1-card__meta">
        <span>
          <b>Status:</b> {card.statusLabel}
        </span>
        <span className="p1-card__sep" aria-hidden="true" />
        <span>
          <b>Nível atual:</b> {card.chain.steps.find((s) => s.current)?.levelLabel ?? '—'}
        </span>
        {card.engagedByUserId !== null && (
          <>
            <span className="p1-card__sep" aria-hidden="true" />
            <span>
              <b>Reconhecido por:</b> {engagedByName ?? '—'}
            </span>
          </>
        )}
      </div>

      {card.bypassedOffline && (
        <div className="p1-card__special">
          ⚠ Atendente <b>OFFLINE</b> · o sistema escalou DIRETAMENTE para o supervisor sem passar
          pelo atendente.
        </div>
      )}

      {card.isLevel4 && (
        <section className="p1-card__section">
          {timelineVM !== null ? (
            <SequenceTimelineMini timeline={timelineVM} />
          ) : (
            <div className="tweaks-hint">
              {timeline.isLoading
                ? 'Carregando sequência…'
                : 'Sequência não disponível para este incidente.'}
            </div>
          )}
        </section>
      )}

      <section className="p1-card__section">
        <div className="p1-card__section-title">Cadeia de escalonamento proposta</div>
        <EscalationChainCard chain={card.chain} />
      </section>

      {interactive && (
        <footer className="p1-card__foot">
          {canAcknowledge && onAcompanhar !== undefined && (
            <button
              className="btn-ghost-mini"
              type="button"
              disabled={acknowledging}
              onClick={() => {
                onAcompanhar(card);
              }}
            >
              {acknowledging ? 'Acompanhando…' : 'Acompanhar'}
            </button>
          )}
          {/* Item 330 — Substituir atendente = reatribuir a conversa do incidente (pelo drawer).
              P19: oculto sem a cap conversation-assign (não morto/desabilitado). */}
          {showReassign && (
            <button
              className="btn-ghost-mini"
              type="button"
              onClick={() => {
                onReassign?.(card);
              }}
            >
              Substituir atendente
            </button>
          )}
          {showRunbook && (
            <button
              className="btn-ghost-mini btn-ghost-mini--primary"
              type="button"
              onClick={() => {
                onRunbook?.(card);
              }}
            >
              Acionar runbook
            </button>
          )}
          {onOpenDetail !== undefined && (
            <button
              className="btn-ghost-mini"
              type="button"
              onClick={() => {
                onOpenDetail(card);
              }}
            >
              Detalhes
            </button>
          )}
          {onOpenConversation !== undefined && (
            <button
              className="btn-ghost-mini"
              type="button"
              onClick={() => {
                onOpenConversation(card.conversationId);
              }}
            >
              Abrir conversa
            </button>
          )}
        </footer>
      )}
    </article>
  );
}
